import { useState, type FormEvent } from 'react'
import { FiEye, FiEyeOff } from 'react-icons/fi'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from '../i18n/LanguageContext'
import { useAuth, ApiError } from '../contexts/AuthContext'
import { auth as authApi } from '../api/client'
import { useMobile } from '../hooks/useMobile'
import TermsContent from './TermsContent'

type Tab = 'login' | 'signup' | 'verify-pending' | 'forgot' | 'forgot-sent'

interface AuthPageProps {
  initialTab?: 'login' | 'signup'
}

export default function AuthPage({ initialTab = 'login' }: AuthPageProps) {
  const { t } = useTranslation()
  const { login, register } = useAuth()
  const isMobile = useMobile()
  const navigate = useNavigate()
  const a = t.auth

  const [tab,           setTab]           = useState<Tab>(initialTab)
  const [name,          setName]          = useState('')
  const [email,         setEmail]         = useState('')
  const [password,      setPassword]      = useState('')
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [showTerms,     setShowTerms]     = useState(false)
  const [error,         setError]         = useState('')
  const [isSubmitting,  setIsSubmitting]  = useState(false)
  const [showPassword,  setShowPassword]  = useState(false)

  // Verify-pending state
  const [pendingEmail,   setPendingEmail]   = useState('')
  const [resendMsg,      setResendMsg]      = useState('')
  const [resending,      setResending]      = useState(false)

  // Forgot password state
  const [forgotEmail,   setForgotEmail]   = useState('')
  const [forgotSending, setForgotSending] = useState(false)

  const switchAuth = (next: 'login' | 'signup') => {
    setTab(next)
    setError('')
    setAcceptedTerms(false)
    navigate(next === 'signup' ? '/signup' : '/login', { replace: true })
  }

  const validate = (): string => {
    if (tab === 'signup' && !name.trim()) return a.errGeneric
    if (!email.includes('@'))             return a.errInvalidCreds
    if (password.length < 8)             return a.errPasswordMin
    if (tab === 'signup' && !acceptedTerms) return a.termsRequired
    return ''
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    const validationError = validate()
    if (validationError) { setError(validationError); return }

    setError('')
    setIsSubmitting(true)
    try {
      if (tab === 'login') {
        await login(email, password)
      } else {
        await register(email, password, name.trim() || undefined)
        setPendingEmail(email)
        setTab('verify-pending')
      }
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401)      setError(a.errInvalidCreds)
        else if (err.status === 403) setError(a.errNotVerified)
        else if (err.status === 409) setError(a.errEmailExists)
        else                          setError(a.errGeneric)
      } else {
        setError(a.errGeneric)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleResend = async () => {
    setResending(true)
    setResendMsg('')
    try {
      await authApi.resendVerification(pendingEmail)
      setResendMsg(a.verifyResendSent)
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setResendMsg(a.verifyResendWait)
      } else {
        setResendMsg(a.verifyResendSent) // same generic message for security
      }
    } finally {
      setResending(false)
    }
  }

  const handleForgot = async (e: FormEvent) => {
    e.preventDefault()
    if (!forgotEmail.includes('@')) return
    setForgotSending(true)
    try {
      await authApi.forgotPassword(forgotEmail)
      setTab('forgot-sent')
    } catch {
      setTab('forgot-sent') // always show the same message
    } finally {
      setForgotSending(false)
    }
  }

  const isLogin = tab === 'login'

  // ── Verify pending screen ─────────────────────────────────────
  if (tab === 'verify-pending') {
    return (
      <div style={r(s.root, isMobile && sm.root)}>
        <MobileBanner isMobile={isMobile} name={t.app.name} />
        <div style={!isMobile ? s.panel : undefined} aria-hidden={isMobile}>
          {!isMobile && <PanelContent name={t.app.name} tagline={t.app.tagline} />}
        </div>
        <div style={r(s.form, isMobile && sm.form)}>
          <div style={s.centeredBlock}>
            <span style={s.bigEmoji}>📬</span>
            <h2 style={s.formHeading}>{a.verifyTitle}</h2>
            <p style={s.formSubtitle}>{a.verifySubtitle}</p>
            <p style={{ ...s.formSubtitle, marginTop: 8, fontSize: '0.9rem', color: '#9A8070' }}>
              {pendingEmail}
            </p>
            <button
              onClick={handleResend}
              disabled={resending}
              style={{ ...s.outlineBtn, marginTop: 28 }}
            >
              {resending ? '…' : a.verifyResend}
            </button>
            {resendMsg && <p style={s.infoText}>{resendMsg}</p>}
            <button onClick={() => switchAuth('login')} style={s.switchBtn}>{a.backToLogin}</button>
          </div>
        </div>
      </div>
    )
  }

  // ── Forgot password — sent screen ────────────────────────────
  if (tab === 'forgot-sent') {
    return (
      <div style={r(s.root, isMobile && sm.root)}>
        <MobileBanner isMobile={isMobile} name={t.app.name} />
        <div style={!isMobile ? s.panel : undefined} aria-hidden={isMobile}>
          {!isMobile && <PanelContent name={t.app.name} tagline={t.app.tagline} />}
        </div>
        <div style={r(s.form, isMobile && sm.form)}>
          <div style={s.centeredBlock}>
            <span style={s.bigEmoji}>📩</span>
            <h2 style={s.formHeading}>{a.forgotTitle}</h2>
            <p style={s.formSubtitle}>{a.forgotSent}</p>
            <button onClick={() => switchAuth('login')} style={{ ...s.outlineBtn, marginTop: 28 }}>
              {a.backToLogin}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── Forgot password — form ────────────────────────────────────
  if (tab === 'forgot') {
    return (
      <div style={r(s.root, isMobile && sm.root)}>
        <MobileBanner isMobile={isMobile} name={t.app.name} />
        <div style={!isMobile ? s.panel : undefined} aria-hidden={isMobile}>
          {!isMobile && <PanelContent name={t.app.name} tagline={t.app.tagline} />}
        </div>
        <div style={r(s.form, isMobile && sm.form)}>
          <div style={{ marginBottom: 32 }}>
            <h2 style={s.formHeading}>{a.forgotTitle}</h2>
            <p style={s.formSubtitle}>{a.forgotSubtitle}</p>
          </div>
          <form onSubmit={handleForgot} noValidate style={s.formInner}>
            <div style={s.field}>
              <label style={r(s.label, isMobile && sm.label)}>{a.emailLabel}</label>
              <input
                className="auth-input"
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder={a.emailPlaceholder}
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                disabled={forgotSending}
              />
            </div>
            <button type="submit" disabled={forgotSending} style={s.submitBtn}>
              {forgotSending ? a.forgotSending : a.forgotBtn}
            </button>
          </form>
          <button onClick={() => switchAuth('login')} style={s.switchBtn}>{a.backToLogin}</button>
        </div>
      </div>
    )
  }

  // ── Login / Signup ────────────────────────────────────────────
  const btnLabel = isSubmitting
    ? (isLogin ? a.loggingIn : a.signingUp)
    : (isLogin ? a.loginBtn  : a.signupBtn)

  return (
    <div style={r(s.root, isMobile && sm.root)}>
      {/* Mobile: slim banner instead of full panel */}
      <MobileBanner isMobile={isMobile} name={t.app.name} />

      {/* Desktop: full left panel */}
      {!isMobile && (
        <div style={s.panel} aria-hidden>
          <div style={s.panelGlow1} />
          <div style={s.panelGlow2} />
          <span className="auth-float" style={{ ...s.floatEl, top: '12%',    left: '10%',  fontSize: '3.5rem', '--dur': '7s' } as React.CSSProperties}>🌿</span>
          <span className="auth-float" style={{ ...s.floatEl, top: '22%',    right: '12%', fontSize: '2.2rem', '--dur': '9s',  animationDelay: '-3s' } as React.CSSProperties}>🌸</span>
          <span className="auth-float" style={{ ...s.floatEl, bottom: '28%', left: '8%',   fontSize: '2.8rem', '--dur': '8s',  animationDelay: '-1.5s' } as React.CSSProperties}>🦋</span>
          <span className="auth-float" style={{ ...s.floatEl, bottom: '14%', right: '10%', fontSize: '2rem',   '--dur': '11s', animationDelay: '-5s' } as React.CSSProperties}>🌺</span>
          <span className="auth-float" style={{ ...s.floatEl, top: '52%',    left: '50%',  fontSize: '1.6rem', '--dur': '13s', animationDelay: '-2s', transform: 'translateX(-50%)' } as React.CSSProperties}>⭐</span>
          <PanelContent name={t.app.name} tagline={t.app.tagline} />
        </div>
      )}

      {/* Form panel */}
      <div style={r(s.form, isMobile && sm.form)}>
        {/* Tab switcher */}
        <div style={r(s.tabs, isMobile && sm.tabs)} role="tablist">
          <button
            role="tab"
            aria-selected={isLogin}
            onClick={() => switchAuth('login')}
            style={r(s.tab, isMobile && sm.tab, isLogin && s.tabActive)}
          >
            {a.loginTab}
          </button>
          <button
            role="tab"
            aria-selected={!isLogin}
            onClick={() => switchAuth('signup')}
            style={r(s.tab, isMobile && sm.tab, !isLogin && s.tabActive)}
          >
            {a.signupTab}
          </button>
        </div>

        {/* Heading */}
        <div className="fade-up" style={{ marginBottom: 28 }}>
          <h2 style={s.formHeading}>
            {isLogin ? a.loginWelcome : a.signupWelcome}
          </h2>
          <p style={s.formSubtitle}>
            {isLogin ? a.loginSubtitle : a.signupSubtitle}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} noValidate style={s.formInner}>
          {!isLogin && (
            <div className="fade-up" style={s.field}>
              <label htmlFor="auth-name" style={r(s.label, isMobile && sm.label)}>{a.nameLabel}</label>
              <input
                id="auth-name"
                className={`auth-input${error && !name.trim() ? ' error' : ''}`}
                type="text"
                autoComplete="name"
                placeholder={a.namePlaceholder}
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
          )}

          <div className="fade-up" style={s.field}>
            <label htmlFor="auth-email" style={r(s.label, isMobile && sm.label)}>{a.emailLabel}</label>
            <input
              id="auth-email"
              className="auth-input"
              type="email"
              autoComplete="email"
              inputMode="email"
              placeholder={a.emailPlaceholder}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isSubmitting}
            />
          </div>

          <div className="fade-up" style={s.field}>
            <div style={s.labelRow}>
              <label htmlFor="auth-password" style={r(s.label, isMobile && sm.label)}>{a.passwordLabel}</label>
              {isLogin && (
                <button
                  type="button"
                  onClick={() => { setForgotEmail(email); setTab('forgot') }}
                  style={s.forgotLink}
                >
                  {a.forgotLink}
                </button>
              )}
            </div>
            <div style={{ position: 'relative' }}>
              <input
                id="auth-password"
                className={`auth-input${error === a.errPasswordMin ? ' error' : ''}`}
                type={showPassword ? 'text' : 'password'}
                autoComplete={isLogin ? 'current-password' : 'new-password'}
                placeholder={a.passwordPlaceholder}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isSubmitting}
                style={{ paddingRight: 48 }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#6B4C3B', padding: 4, minHeight: 'unset' }}
                aria-label={showPassword ? a.hidePassword : a.showPassword}
              >
                {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
              </button>
            </div>
          </div>

          {!isLogin && (
            <div style={s.termsRow} className="fade-up">
              <input
                id="auth-terms"
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                disabled={isSubmitting}
                style={s.termsCheckbox}
              />
              <label htmlFor="auth-terms" style={r(s.termsLabel, isMobile && sm.termsLabel)}>
                {a.termsAccept}{' '}
                <button
                  type="button"
                  onClick={() => setShowTerms(true)}
                  style={s.termsLink}
                >
                  {a.termsLink}
                </button>
              </label>
            </div>
          )}

          {error && (
            <div role="alert" style={s.errorBox}>
              <span style={s.errorIcon}>⚠</span> {error}
              {/* If not-verified error, offer resend inline */}
              {error === a.errNotVerified && (
                <button
                  type="button"
                  onClick={() => { setPendingEmail(email); setTab('verify-pending') }}
                  style={s.inlineLink}
                >
                  {a.verifyResend}
                </button>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting || (!isLogin && !acceptedTerms)}
            style={{ ...s.submitBtn, opacity: (isSubmitting || (!isLogin && !acceptedTerms)) ? 0.75 : 1, cursor: (isSubmitting || (!isLogin && !acceptedTerms)) ? 'not-allowed' : 'pointer' }}
          >
            {btnLabel}
          </button>
        </form>

        <button onClick={() => switchAuth(isLogin ? 'signup' : 'login')} style={s.switchBtn}>
          {isLogin ? a.switchToSignup : a.switchToLogin}
        </button>

        <p style={s.formFooter}>{t.app.footer}</p>
      </div>

      {/* Terms modal */}
      {showTerms && (
        <div
          style={s.modalOverlay}
          role="dialog"
          aria-modal="true"
          aria-label={a.termsLink}
          onClick={(e) => { if (e.target === e.currentTarget) setShowTerms(false) }}
        >
          <div style={r(s.modalSheet, isMobile && sm.modalSheet)}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>{a.termsLink}</h2>
              <button
                onClick={() => setShowTerms(false)}
                style={s.modalClose}
                aria-label={t.terms.backBtn}
              >
                ✕
              </button>
            </div>
            <div style={s.modalBody}>
              <TermsContent />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/* ── Sub-components ──────────────────────────────────────────── */

function MobileBanner({ isMobile, name }: { isMobile: boolean; name: string }) {
  if (!isMobile) return null
  return (
    <div style={sm.banner}>
      <span style={sm.bannerLeaf}>🌿</span>
      <span style={sm.bannerName}>{name}</span>
    </div>
  )
}

function PanelContent({ name, tagline }: { name: string; tagline: string }) {
  return (
    <div style={s.panelContent}>
      <div style={s.panelBrand}>
        <span style={s.panelLeaf}>🌿</span>
        <h1 style={s.panelName}>{name}</h1>
      </div>
      <p style={s.panelTagline}>{tagline}</p>
      <div style={s.dotRow} aria-hidden>
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} style={{ ...s.dot, opacity: 0.3 + i * 0.14 }} />
        ))}
      </div>
    </div>
  )
}

/* ── Helper: merge style objects ─────────────────────────────── */
function r(...styles: (React.CSSProperties | undefined | false)[]): React.CSSProperties {
  return Object.assign({}, ...styles.filter(Boolean))
}

/* ── Desktop styles ──────────────────────────────────────────── */
const s: Record<string, React.CSSProperties> = {
  root: {
    display:       'flex',
    minHeight:     '100vh',
    flexDirection: 'row',
  },

  panel: {
    position:       'relative',
    width:          '42%',
    flexShrink:     0,
    background:     'linear-gradient(150deg, #163528 0%, #1B4332 40%, #2D6A4F 100%)',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    overflow:       'hidden',
  },
  panelGlow1: {
    position: 'absolute', top: '-80px', right: '-80px',
    width: '320px', height: '320px', borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(82,183,120,0.18) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  panelGlow2: {
    position: 'absolute', bottom: '-60px', left: '-60px',
    width: '280px', height: '280px', borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(212,160,23,0.12) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  floatEl: { position: 'absolute', pointerEvents: 'none', userSelect: 'none', opacity: 0.22, lineHeight: 1 },
  panelContent: { position: 'relative', zIndex: 1, textAlign: 'center', padding: '0 40px' },
  panelBrand:   { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 16 },
  panelLeaf:    { fontSize: '2.4rem', filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.3))' },
  panelName: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize:   'clamp(2.2rem, 4vw, 3.6rem)',
    fontWeight: 700, color: '#FFFFFF', lineHeight: 1, letterSpacing: '-0.02em',
    textShadow: '0 2px 16px rgba(0,0,0,0.25)',
  },
  panelTagline: {
    fontFamily: 'Nunito, sans-serif', fontSize: '1rem', fontWeight: 500,
    color: 'rgba(255,255,255,0.68)', lineHeight: 1.5, maxWidth: 260, margin: '0 auto 36px',
  },
  dotRow: { display: 'flex', justifyContent: 'center', gap: 8, marginTop: 36 },
  dot:    { width: 8, height: 8, borderRadius: '50%', background: '#FFFFFF' },

  form: {
    flex:          1,
    display:       'flex',
    flexDirection: 'column',
    justifyContent:'center',
    padding:       'clamp(32px, 6vw, 80px) clamp(28px, 6vw, 72px)',
    background:    '#FAF6EF',
    overflowY:     'auto',
  },

  tabs: {
    display:      'flex',
    gap:          4,
    background:   '#EFE7D7',
    borderRadius: 14,
    padding:      4,
    marginBottom: 32,
    width:        'fit-content',
  },
  tab: {
    fontFamily: 'Nunito, sans-serif',
    fontSize:   '1rem',
    fontWeight: 700,
    color:      '#9A8070',
    padding:    '10px 22px',
    borderRadius: 10,
    background: 'transparent',
    border:     'none',
    cursor:     'pointer',
    whiteSpace: 'nowrap',
    minHeight:  48,
  },
  tabActive: { background: '#FFFFFF', color: '#1B4332', boxShadow: '0 2px 10px rgba(27,67,50,0.10)' },

  formHeading: {
    fontFamily:   "'Cormorant Garamond', serif",
    fontSize:     'clamp(2rem, 3.5vw, 2.8rem)',
    fontWeight:   700,
    color:        '#1B4332',
    lineHeight:   1.1,
    marginBottom: 8,
  },
  formSubtitle: { fontSize: '1.05rem', color: '#6B4C3B', fontWeight: 500 },

  formInner: { display: 'flex', flexDirection: 'column', gap: 20, marginBottom: 24 },
  field:     { display: 'flex', flexDirection: 'column', gap: 8 },

  labelRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 },

  label: {
    fontFamily:    'Nunito, sans-serif',
    fontSize:      '0.9rem',
    fontWeight:    700,
    color:         '#6B4C3B',
    letterSpacing: '0.03em',
    textTransform: 'uppercase',
  },

  forgotLink: {
    background:  'transparent',
    border:      'none',
    color:       '#2D6A4F',
    fontSize: '0.875rem',
    fontWeight:  600,
    cursor:      'pointer',
    padding:     '4px 0',
    minHeight:   44,
    textDecoration: 'underline',
    textUnderlineOffset: '3px',
  },

  errorBox: {
    display:    'flex',
    alignItems: 'flex-start',
    gap:        8,
    background: '#FFF4EE',
    border:     '1.5px solid #F2C8B3',
    borderRadius: 10,
    padding:    '12px 16px',
    fontSize:   '0.95rem',
    fontWeight: 600,
    color:      '#B5541A',
    flexWrap:   'wrap',
  },
  errorIcon: { fontSize: '1rem', flexShrink: 0 },

  inlineLink: {
    background:  'transparent',
    border:      'none',
    color:       '#B5541A',
    fontWeight:  700,
    fontSize:    '0.9rem',
    cursor:      'pointer',
    textDecoration: 'underline',
    padding:     0,
    minHeight:   44,
  },

  submitBtn: {
    width:         '100%',
    padding:       '18px',
    fontSize:      '1.1rem',
    fontWeight:    800,
    color:         '#FFFFFF',
    background:    'linear-gradient(135deg, #1B4332, #2D6A4F)',
    borderRadius:  14,
    border:        'none',
    boxShadow:     '0 6px 20px rgba(27,67,50,0.22)',
    letterSpacing: '0.02em',
    minHeight:     56,
    marginTop:     4,
    cursor:        'pointer',
  },

  outlineBtn: {
    padding:      '14px 28px',
    fontSize:     '1rem',
    fontWeight:   700,
    color:        '#1B4332',
    background:   'transparent',
    border:       '2px solid #1B4332',
    borderRadius: 12,
    cursor:       'pointer',
    minHeight:    48,
  },

  switchBtn: {
    background:  'transparent',
    border:      'none',
    color:       '#2D6A4F',
    fontSize:    '0.95rem',
    fontWeight:  700,
    cursor:      'pointer',
    textAlign:   'center',
    padding:     '12px 0',
    textDecoration: 'underline',
    textUnderlineOffset: '3px',
    minHeight:   48,
  },

  formFooter: {
    marginTop:  32,
    fontSize: '0.875rem',
    color:      '#C0AFA0',
    fontWeight: 500,
    textAlign:  'center',
    letterSpacing: '0.04em',
  },

  termsRow: {
    display:    'flex',
    alignItems: 'flex-start',
    gap:        12,
  },
  termsCheckbox: {
    width:      20,
    height:     20,
    marginTop:  2,
    flexShrink: 0,
    cursor:     'pointer',
    accentColor: '#2D6A4F',
  },
  termsLabel: {
    fontSize:   '0.9rem',
    fontWeight: 500,
    color:      '#5A4A3A',
    lineHeight: 1.5,
    cursor:     'pointer',
  },
  termsLink: {
    background:          'transparent',
    border:              'none',
    color:               '#2D6A4F',
    fontSize:            '0.9rem',
    fontWeight:          700,
    cursor:              'pointer',
    textDecoration:      'underline',
    textUnderlineOffset: '3px',
    padding:             '0',
    minHeight:           'unset',
    display:             'inline',
  },

  // Centred info screens (verify-pending, forgot-sent)
  centeredBlock: {
    display:       'flex',
    flexDirection: 'column',
    alignItems:    'center',
    textAlign:     'center',
    gap:           12,
    maxWidth:      400,
    margin:        '0 auto',
  },
  bigEmoji: { fontSize: '3rem', lineHeight: 1, marginBottom: 8 },
  infoText:  { fontSize: '0.9rem', color: '#2D6A4F', fontWeight: 700, marginTop: 8 },

  // Terms modal
  modalOverlay: {
    position:       'fixed',
    inset:          0,
    background:     'rgba(0,0,0,0.55)',
    zIndex:         1000,
    display:        'flex',
    alignItems:     'flex-end',
    justifyContent: 'center',
  },
  modalSheet: {
    background:   '#FAF6EF',
    borderRadius: '16px 16px 0 0',
    width:        '100%',
    maxWidth:     680,
    maxHeight:    '88vh',
    display:      'flex',
    flexDirection:'column',
    overflow:     'hidden',
  },
  modalHeader: {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'space-between',
    padding:        '20px 24px 16px',
    borderBottom:   '1px solid #E5DDD0',
    flexShrink:     0,
  },
  modalTitle: {
    fontSize:   '1.25rem',
    fontWeight: 700,
    color:      '#1B4332',
    margin:     0,
  },
  modalClose: {
    background: 'none',
    border:     'none',
    cursor:     'pointer',
    fontSize:   '1.25rem',
    color:      '#6B7280',
    minHeight:  48,
    minWidth:   48,
    display:    'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalBody: {
    overflowY: 'auto',
    padding:   '20px 24px 40px',
    flex:      1,
  },
}

/* ── Mobile overrides ────────────────────────────────────────── */
const sm: Record<string, React.CSSProperties> = {
  root: { flexDirection: 'column' },

  banner: {
    background:     'linear-gradient(135deg, #1B4332, #2D6A4F)',
    padding:        '16px 20px',
    display:        'flex',
    alignItems:     'center',
    gap:            10,
    flexShrink:     0,
  },
  bannerLeaf: { fontSize: '1.6rem' },
  bannerName: {
    fontFamily:  "'Cormorant Garamond', serif",
    fontSize:    '1.5rem',
    fontWeight:  700,
    color:       '#FFFFFF',
    letterSpacing: '-0.01em',
  },

  form: {
    padding:        '28px 20px 40px',
    justifyContent: 'flex-start',
  },

  tabs: {
    width:        '100%',
    marginBottom: 28,
  },

  tab: {
    flex:    1,
    padding: '14px 0',
    textAlign: 'center',
  },

  label:      { fontSize: '1rem' },
  termsLabel: { fontSize: '1rem' },

  modalSheet: {
    maxHeight:    '92vh',
    borderRadius: '16px 16px 0 0',
  },
}
